"use client";

import { useMemo, useState } from "react";
import { Link } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    AUTORISATION_ANNEES,
    AUTORISATION_RENOUVELLEMENT_ANNEES,
    DELAI_ACCESSOIRE_MOIS,
    DELAI_TEMPS_PARTIEL_MOIS,
    EXCLUSIVITE_LEVEE_MOIS,
    NOUVELLE_AUTORISATION_ANNEES,
    QUOTITE_MINIMALE,
    SEUIL_DECLARATION_HEURES,
    SEUIL_DECLARATION_POURCENT,
    recapitulatif,
    valider,
    verdict,
} from "@/lib/tools/employmentCombination";
import {
    Avancer,
    Choix,
    Ecran,
    Liste,
    Recommencer,
    Resultat,
    remplir,
    useParcours,
} from "./Journey";

/**
 * Garder son emploi et lancer son activité.
 *
 * LA QUESTION D'AVANT TOUTES LES AUTRES, et la rubrique ne la posait nulle
 * part : vingt-neuf outils parlaient à qui avait déjà créé son entreprise, ou
 * décidé de le faire. Celui-ci s'adresse à qui apprend le soir et n'a pas
 * encore quitté son poste.
 *
 * DEUX BRANCHES QUI NE SE RESSEMBLENT PAS, et c'est tout l'intérêt. Le salarié
 * du privé n'a aucune autorisation à demander, l'agent public en a une à
 * obtenir. Les pages qui traitent les deux d'un même mouvement font renoncer
 * des agents qui avaient une voie ouverte, et lancent des salariés dans une
 * démarche qui n'existe pas.
 *
 * AUCUN VERDICT AVANT LA DERNIÈRE RÉPONSE. Le module rend `null` tant qu'une
 * question de la branche reste ouverte, et l'écran de résultat nomme alors ce
 * qui manque plutôt que de conclure à moitié.
 */

const L = {
    fr: {
        phases: ["Votre employeur", "Votre situation", "Ce que vous pouvez faire"],
        retour: "Retour",
        prive: "Rien ne sort de cet onglet : le diagnostic se fait dans votre navigateur, et tout s'efface quand vous le fermez.",
        recommencer: "Recommencer",

        employeurTitre: "Qui est votre employeur aujourd'hui ?",
        employeurAide: "Dans le privé, le cumul est libre et sans démarche. Dans le public, il est interdit sauf autorisation.",
        employeurPriveTitre: "Une entreprise privée",
        employeurPriveDetail: "CDI, CDD, intérim, association",
        employeurPublicTitre: "Une administration",
        employeurPublicDetail: "Fonctionnaire ou contractuel, dans les trois fonctions publiques",
        employeurAucunTitre: "Je n'ai pas d'employeur",
        employeurAucunDetail: "Sans emploi, étudiant, déjà indépendant",

        tempsTitre: "Votre contrat est-il à temps plein ?",
        tempsAide: "Une clause d'exclusivité ne peut pas être imposée à un salarié embauché à temps partiel.",
        tempsPleinTitre: "Temps plein",
        tempsPleinDetail: "La durée de mon contrat est celle d'un temps complet",
        tempsPartielTitre: "Temps partiel",
        tempsPartielDetail: "Mon contrat prévoit moins qu'un temps complet",

        exclusiviteTitre: "Votre contrat contient-il une clause d'exclusivité ?",
        exclusiviteAide: "Elle interdit toute autre activité professionnelle. Cherchez « exclusivité » dans votre contrat ou dans votre convention collective.",
        exclusiviteOuiTitre: "Oui, il y en a une",
        exclusiviteOuiDetail: "Je l'ai lue dans mon contrat",
        exclusiviteNonTitre: "Non, aucune",
        exclusiviteNonDetail: "J'ai vérifié, mon contrat n'en parle pas",
        exclusiviteInconnuTitre: "Je ne sais pas",
        exclusiviteInconnuDetail: "Je n'ai pas relu mon contrat",

        concurrenceTitre: "Votre activité s'adresserait-elle au marché de votre employeur ?",
        concurrenceAide: "Le même métier, les mêmes clients, ou les clients de votre employeur démarchés directement.",
        concurrenceOuiTitre: "Oui, au moins en partie",
        concurrenceOuiDetail: "Même métier, ou ses clients",
        concurrenceNonTitre: "Non, rien à voir",
        concurrenceNonDetail: "Un autre métier, d'autres clients",
        concurrenceInconnuTitre: "C'est proche, sans être le même",
        concurrenceInconnuDetail: "Un métier voisin, un marché qui se touche",

        momentTitre: "Quand comptez-vous travailler à votre activité ?",
        momentAide: "Les moyens comptent aussi : l'ordinateur, la messagerie et les fichiers de votre employeur restent les siens, même en dehors de vos heures.",
        momentHorsTitre: "En dehors de mon travail",
        momentHorsDetail: "Le soir, le week-end, sur mes congés, avec mes moyens",
        momentPendantTitre: "Parfois pendant mes heures",
        momentPendantDetail: "Sur mon temps de travail, ou avec le matériel de l'entreprise",

        quotiteTitre: "Combien d'heures votre poste prévoit-il par semaine ?",
        quotiteAide: "Le seuil de {heures} heures, soit {pourcent} % de la durée légale, sépare une simple déclaration d'une autorisation à obtenir.",
        quotitePleinTitre: "Plus de {heures} heures",
        quotitePleinDetail: "Temps complet, ou proche du temps complet",
        quotiteReduitTitre: "{heures} heures ou moins",
        quotiteReduitDetail: "Un emploi à temps non complet ou incomplet",

        natureTitre: "Que voulez-vous faire ?",
        natureAide: "Les règles ne sont pas les mêmes pour une entreprise et pour une activité exercée à titre accessoire.",
        natureEntrepriseTitre: "Créer mon entreprise",
        natureEntrepriseDetail: "Une activité à moi, qui dure",
        natureAccessoireTitre: "Une activité accessoire",
        natureAccessoireDetail: "Enseignement, expertise, services à la personne, sport, culture",
        natureVenteBiensTitre: "Vendre ce que je fabrique",
        natureVenteBiensDetail: "Des biens produits par moi-même",

        verdictOuvert: "Vous pouvez vous lancer",
        verdictConditionne: "Vous pouvez vous lancer, sous conditions",
        verdictFerme: "Pas en l'état",
        verdictHorsSujet: "Vous n'êtes pas concerné",

        resultatTitre: "Votre situation",
        motifsTitre: "Pourquoi",
        recapTitre: "Ce que vous avez répondu",
        demarchesTitre: "Ce que vous avez à faire",
        alertesTitre: "À surveiller",
        suiteTitre: "Pour aller plus loin",
        manquesTitre: "Il manque une réponse",

        // Motifs, côté privé.
        aucuneClause: "Aucune clause d'exclusivité ne vous lie : rien dans le code du travail n'interdit à un salarié d'exercer une activité indépendante à côté de son emploi.",
        exclusiviteLeveeUnAn: "Une clause d'exclusivité ne vous est pas opposable pendant {mois} mois à partir de la création de votre entreprise, même si votre contrat en porte une (article L1222-5 du code du travail). Les voyageurs, représentants et placiers font seuls exception.",
        exclusiviteInopposableTempsPartiel: "Votre contrat est à temps partiel : une clause d'exclusivité ne peut pas vous être imposée.",
        aucuneAutorisation: "Aucune loi ne vous impose de demander une autorisation ni de faire une déclaration à votre employeur. Votre contrat ou votre convention collective peuvent en décider autrement : relisez-les. Et dans l'artisanat, travailler pour les clients de votre employeur demande son accord.",
        loyautePermanente: "L'obligation de loyauté, elle, ne se lève jamais : elle vaut sans clause, et la levée de l'exclusivité n'y touche pas.",
        dureesMaximalesNonApplicables: "Les durées maximales de travail, 10 heures par jour et 48 heures par semaine, comptent vos emplois salariés. Votre activité indépendante n'y entre pas.",
        concurrenceInterdite: "Une activité qui s'adresse au marché de votre employeur est une faute, avec ou sans clause. C'est le motif de licenciement le plus fréquent dans cette situation.",
        horsTempsDeTravail: "Votre activité doit s'exercer en dehors de votre temps de travail, et sans les moyens de votre employeur. Aucun texte ne prévoit de tolérance sur ce point.",

        // Motifs, côté public.
        interdictionDePrincipe: "Un agent public ne peut pas exercer une activité privée lucrative à titre professionnel : c'est l'interdiction de principe posée par l'article L123-1 du code général de la fonction publique. Tout le reste est une exception.",
        declarationSuffit: "Votre poste est à temps non complet : une déclaration écrite à votre autorité hiérarchique suffit, sans autorisation à obtenir.",
        tempsPartielExige: "Créer votre entreprise suppose de passer à temps partiel, à {quotite} % au moins d'un temps plein, sur autorisation.",
        activiteAccessoire: "Une activité accessoire s'exerce sur autorisation préalable de votre autorité hiérarchique.",
        venteBiensEnMicro: "La vente de biens que vous produisez vous-même s'exerce obligatoirement sous le statut de micro-entrepreneur.",
        horsHeuresDeService: "Dans tous les cas, votre activité s'exerce en dehors de vos heures de service.",
        sansEmployeur: "La question du cumul ne se pose qu'à qui a un employeur. Sans emploi salarié ni poste public, rien ne vous retient.",

        // Démarches.
        declarerActivite: "Déclarez votre activité au guichet unique : c'est la seule formalité qui vous attend, et elle ne regarde pas votre employeur.",
        declarationEcrite: "Adressez une déclaration écrite à votre autorité hiérarchique avant de commencer.",
        demandeTempsPartiel: "Demandez à passer à temps partiel, à {quotite} % au moins. L'administration répond sous {delaiPartiel} mois.",
        autorisationHierarchique: "Demandez l'autorisation par écrit, en décrivant l'activité, son employeur éventuel et sa rémunération. La réponse vient sous {delaiAccessoire} mois, deux si un complément vous est demandé.",
        revoirOrganisation: "Reportez votre activité en dehors de vos heures, et servez-vous de vos propres moyens. C'est la condition qui manque, et elle se règle sans démarche.",

        // Alertes.
        echeanceUnAn: "Passé {mois} mois, votre clause d'exclusivité redevient opposable. Notez la date dès votre déclaration d'activité.",
        relireLeContrat: "Relisez votre contrat avant de vous lancer. Même si une clause d'exclusivité ne joue pas la première année, vous devez savoir ce qu'elle dit pour la suite.",
        clauseATempsPartielAVerifier: "Une clause d'exclusivité dans un contrat à temps partiel ne tient que si elle est indispensable à la protection des intérêts de l'entreprise et justifiée par vos fonctions. Faites-la lire avant de renoncer à quoi que ce soit.",
        concurrenceADeterminer: "Un marché voisin se juge sur les faits, pas sur l'intitulé de votre activité. Écrivez noir sur blanc ce que vous vendrez et à qui, et comparez-le à l'objet de votre employeur.",
        requalificationSiEmployeurClient: "Si votre employeur devenait votre client, vous risqueriez une requalification en salariat.",
        dureeAutorisation: "L'autorisation court {annees} ans, renouvelable {renouvellement} an. Au bout, il faut renoncer à l'activité ou quitter son poste.",
        delaiAvantNouvelleDemande: "Une nouvelle autorisation ne peut pas être accordée moins de {nouvelle} ans après la fin de la précédente.",
        delaiReponse: "Attendez la réponse avant de commencer : une activité exercée sans autorisation est une faute.",

        recapEmployeur: "Employeur",
        recapTemps: "Contrat",
        recapExclusivite: "Clause d'exclusivité",
        recapConcurrence: "Marché visé",
        recapMoment: "Quand",
        recapQuotite: "Heures par semaine",
        recapNature: "Projet",

        exclues: "Quel que soit votre employeur, trois familles d'activités n'entrent pas dans le régime micro : la TVA immobilière, les activités agricoles rattachées à la MSA, et les professions qui relèvent d'une autre caisse de retraite, comme les professions juridiques, médicales, les experts-comptables et les artistes-auteurs.",

        source: "Règles relevées le 23 septembre 2026 sur la fiche du cumul salarié et micro-entrepreneur de service-public.gouv.fr, vérifiée le 10 avril 2026, sur celle du cumul d'activités d'un agent public, et sur le texte des articles cités au Code du travail numérique.",
    },
    en: {
        phases: ["Your employer", "Your situation", "What you can do"],
        retour: "Back",
        prive: "Nothing leaves this tab: the diagnosis runs in your browser, and everything is erased when you close it.",
        recommencer: "Start again",

        employeurTitre: "Who do you work for today?",
        employeurAide: "In the private sector, holding both is free and needs no step. In the public sector, it is forbidden unless authorised.",
        employeurPriveTitre: "A private company",
        employeurPriveDetail: "Permanent, fixed-term, agency work, charity",
        employeurPublicTitre: "A public body",
        employeurPublicDetail: "Civil servant or contract staff, in any of the three public services",
        employeurAucunTitre: "I have no employer",
        employeurAucunDetail: "Out of work, studying, already self-employed",

        tempsTitre: "Is your contract full-time?",
        tempsAide: "An exclusivity clause cannot be imposed on an employee hired part-time.",
        tempsPleinTitre: "Full-time",
        tempsPleinDetail: "My contract is for full hours",
        tempsPartielTitre: "Part-time",
        tempsPartielDetail: "My contract is for less than full hours",

        exclusiviteTitre: "Does your contract carry an exclusivity clause?",
        exclusiviteAide: "It forbids any other professional activity. Look for « exclusivité » in your contract or in your collective agreement.",
        exclusiviteOuiTitre: "Yes, there is one",
        exclusiviteOuiDetail: "I have read it in my contract",
        exclusiviteNonTitre: "No, none",
        exclusiviteNonDetail: "I checked, my contract says nothing of it",
        exclusiviteInconnuTitre: "I do not know",
        exclusiviteInconnuDetail: "I have not read my contract again",

        concurrenceTitre: "Would your activity address your employer's market?",
        concurrenceAide: "The same trade, the same clients, or your employer's clients approached directly.",
        concurrenceOuiTitre: "Yes, at least in part",
        concurrenceOuiDetail: "Same trade, or their clients",
        concurrenceNonTitre: "No, nothing to do with it",
        concurrenceNonDetail: "Another trade, other clients",
        concurrenceInconnuTitre: "Close, without being the same",
        concurrenceInconnuDetail: "A neighbouring trade, a market that touches theirs",

        momentTitre: "When do you intend to work on it?",
        momentAide: "What you work with counts too: your employer's computer, mailbox and files remain theirs, even outside your hours.",
        momentHorsTitre: "Outside my job",
        momentHorsDetail: "Evenings, weekends, holidays, with my own means",
        momentPendantTitre: "Sometimes during my hours",
        momentPendantDetail: "On working time, or with the company's equipment",

        quotiteTitre: "How many hours a week does your post run to?",
        quotiteAide: "The {heures} hour threshold, {pourcent} % of statutory hours, separates a simple declaration from an authorisation to obtain.",
        quotitePleinTitre: "More than {heures} hours",
        quotitePleinDetail: "Full hours, or close to them",
        quotiteReduitTitre: "{heures} hours or fewer",
        quotiteReduitDetail: "A post created for reduced hours",

        natureTitre: "What do you want to do?",
        natureAide: "The rules differ between a business of your own and an activity carried on as a sideline.",
        natureEntrepriseTitre: "Start my business",
        natureEntrepriseDetail: "An activity of my own, meant to last",
        natureAccessoireTitre: "A sideline activity",
        natureAccessoireDetail: "Teaching, expertise, personal services, sport, culture",
        natureVenteBiensTitre: "Sell what I make",
        natureVenteBiensDetail: "Goods produced by myself",

        verdictOuvert: "You can start",
        verdictConditionne: "You can start, under conditions",
        verdictFerme: "Not as things stand",
        verdictHorsSujet: "This does not apply to you",

        resultatTitre: "Your situation",
        motifsTitre: "Why",
        recapTitre: "What you answered",
        demarchesTitre: "What you have to do",
        alertesTitre: "What to watch",
        suiteTitre: "Going further",
        manquesTitre: "An answer is missing",

        aucuneClause: "No exclusivity clause binds you: nothing in the labour code forbids an employee from carrying on a self-employed activity alongside their job.",
        exclusiviteLeveeUnAn: "An exclusivity clause cannot be held against you for {mois} months from the creation of your business, even if your contract carries one (article L1222-5 of the labour code). Travelling sales representatives are the only exception.",
        exclusiviteInopposableTempsPartiel: "Your contract is part-time: an exclusivity clause cannot be imposed on you.",
        aucuneAutorisation: "No law requires you to ask your employer for authorisation or to declare anything to them. Your contract or your collective agreement may decide otherwise: read them again. And in a craft trade, working for your employer's clients requires their consent.",
        loyautePermanente: "The duty of loyalty never lifts: it applies without any clause, and the suspension of exclusivity does not touch it.",
        dureesMaximalesNonApplicables: "The maximum working hours, 10 a day and 48 a week, count your salaried jobs. Your self-employed activity does not enter into them.",
        concurrenceInterdite: "An activity addressing your employer's market is a breach, with or without a clause. It is the most frequent ground for dismissal in this situation.",
        horsTempsDeTravail: "Your activity must be carried on outside your working time, and without your employer's means. No text allows any leeway on this point.",

        interdictionDePrincipe: "A public employee may not carry on a private profit-making activity on a professional basis: that is the prohibition laid down by article L123-1 of the general code of the public service. Everything else is an exception.",
        declarationSuffit: "Your post runs to reduced hours: a written declaration to your line authority is enough, with no authorisation to obtain.",
        tempsPartielExige: "Starting your business means moving to part-time, at {quotite} % of full hours at least, subject to authorisation.",
        activiteAccessoire: "A sideline activity requires the prior authorisation of your line authority.",
        venteBiensEnMicro: "Selling goods you produce yourself must be done under the micro-entrepreneur status.",
        horsHeuresDeService: "In every case, your activity is carried on outside your hours of service.",
        sansEmployeur: "The question of holding both only arises for someone with an employer. With no job and no public post, nothing holds you back.",

        declarerActivite: "Declare your activity at the one-stop shop: it is the only formality ahead of you, and it does not concern your employer.",
        declarationEcrite: "Send a written declaration to your line authority before you begin.",
        demandeTempsPartiel: "Ask to move to part-time, at {quotite} % at least. The administration answers within {delaiPartiel} months.",
        autorisationHierarchique: "Ask for authorisation in writing, describing the activity, any employer and the pay. The answer comes within {delaiAccessoire} month, two if further information is requested.",
        revoirOrganisation: "Move your activity outside your hours, and use your own means. That is the missing condition, and it needs no formality.",

        echeanceUnAn: "After {mois} months, your exclusivity clause can be held against you again. Note the date as soon as you declare your activity.",
        relireLeContrat: "Read your contract again before you start. Even if an exclusivity clause does not apply in the first year, you need to know what it says for afterwards.",
        clauseATempsPartielAVerifier: "An exclusivity clause in a part-time contract only holds if it is essential to protect the company's interests and justified by your duties. Have it read before giving anything up.",
        concurrenceADeterminer: "A neighbouring market is judged on the facts, not on the wording of your activity. Write down plainly what you will sell and to whom, and compare it with your employer's business.",
        requalificationSiEmployeurClient: "Should your employer become your client, you would risk being reclassified as an employee.",
        dureeAutorisation: "The authorisation runs for {annees} years, renewable for {renouvellement} year. At the end, you must give up the activity or leave your post.",
        delaiAvantNouvelleDemande: "A new authorisation cannot be granted less than {nouvelle} years after the previous one ends.",
        delaiReponse: "Wait for the answer before you begin: an activity carried on without authorisation is a breach.",

        recapEmployeur: "Employer",
        recapTemps: "Contract",
        recapExclusivite: "Exclusivity clause",
        recapConcurrence: "Market addressed",
        recapMoment: "When",
        recapQuotite: "Hours a week",
        recapNature: "Plan",

        exclues: "Whoever your employer is, three families of activity fall outside the micro-enterprise scheme: property VAT, farming attached to the MSA, and professions belonging to another pension fund, such as legal and medical professions, chartered accountants and artist-authors.",

        source: "Rules recorded on 23 September 2026 from the service-public.gouv.fr page on holding both employee and micro-entrepreneur status, verified on 10 April 2026, from the page on public employees holding a second activity, and from the text of the articles cited in the digital labour code.",
    },
};

/** Les valeurs que les phrases empruntent au module. */
const VALEURS = {
    mois: EXCLUSIVITE_LEVEE_MOIS,
    quotite: QUOTITE_MINIMALE,
    heures: SEUIL_DECLARATION_HEURES,
    pourcent: SEUIL_DECLARATION_POURCENT,
    annees: AUTORISATION_ANNEES,
    renouvellement: AUTORISATION_RENOUVELLEMENT_ANNEES,
    nouvelle: NOUVELLE_AUTORISATION_ANNEES,
    delaiAccessoire: DELAI_ACCESSOIRE_MOIS,
    delaiPartiel: DELAI_TEMPS_PARTIEL_MOIS,
};

/** L'écran suivant, selon la branche. Le résultat ferme les deux. */
const SUITES = {
    prive: { employeur: "temps", temps: "exclusivite", exclusivite: "concurrence", concurrence: "moment", moment: "resultat" },
    public: { employeur: "quotite", quotite: "nature", nature: "resultat" },
};

const PHASES_PAR_ECRAN = {
    employeur: 0,
    temps: 1,
    exclusivite: 1,
    concurrence: 1,
    moment: 1,
    quotite: 1,
    nature: 1,
    resultat: 2,
};

/** Le ton du verdict. Le refus est ambre, jamais rouge : rien n'est perdu. */
const TONS = {
    ouvert: { icone: "check-circle-linear", couleur: "text-green-600 dark:text-green-400" },
    conditionne: { icone: "clipboard-check-linear", couleur: "text-primary" },
    ferme: { icone: "danger-triangle-linear", couleur: "text-amber-600 dark:text-amber-400" },
    "hors-sujet": { icone: "info-circle-linear", couleur: "text-gray-400 dark:text-lightBlue/60" },
};

/** Les outils vers lesquels le résultat renvoie, selon ce qu'il a conclu. */
const RENVOIS = {
    ouvert: ["revenu-net", "je-me-lance"],
    conditionne: ["revenu-net", "je-me-lance"],
    ferme: ["requalification-salariat", "je-me-lance"],
    "hors-sujet": ["je-me-lance"],
};

const VIDE = {
    employeur: "",
    temps: "",
    exclusivite: "",
    concurrence: "",
    moment: "",
    quotite: "",
    nature: "",
};

export default function CumulEmploi({ lang = "fr", outils = {} }) {
    const t = L[lang] || L.fr;
    const [r, setR] = useState(VIDE);

    const { etape, sens, reduit, rang, aller, retour, recommencer, communs } = useParcours(
        "employeur",
        () => setR(VIDE),
        PHASES_PAR_ECRAN,
        null,
        // Reprendre où l'on en était, le temps de l'onglet (Journey.jsx).
        { r, setR },
    );

    const controle = useMemo(() => valider(r), [r]);
    const conclusion = useMemo(() => verdict(r), [r]);
    const recap = useMemo(() => recapitulatif(r), [r]);
    const commun = { ...communs, phases: t.phases, retour: t.retour, rang };

    /* La branche suit l'employeur, et se lit sur la réponse qu'on vient de
       donner plutôt que sur l'état, qui ne sera à jour qu'au rendu suivant.
       « Je n'ai pas d'employeur » file droit au résultat, qui se déclare hors
       sujet plutôt que de poser cinq questions dont aucune ne le concerne. */
    const repondre = (cle, valeur) => () => {
        setR((p) => ({ ...p, [cle]: valeur }));
        const employeur = cle === "employeur" ? valeur : r.employeur;
        if (employeur === "aucun") return aller("resultat");
        aller(SUITES[employeur]?.[cle] || "resultat");
    };

    const phrase = (cle) => remplir(t[cle], VALEURS);

    if (etape !== "resultat") {
        const choix = CHOIX[etape].map(({ valeur, icone }) => ({
            icone,
            titre: remplir(t[`${etape}${majuscule(valeur)}Titre`], VALEURS),
            detail: remplir(t[`${etape}${majuscule(valeur)}Detail`], VALEURS),
            onClick: repondre(etape, valeur),
        }));

        return (
            <Ecran
                key={etape}
                {...commun}
                phase={PHASES_PAR_ECRAN[etape]}
                onRetour={etape === "employeur" ? undefined : retour}
                titre={t[`${etape}Titre`]}
                aide={remplir(t[`${etape}Aide`], VALEURS)}
            >
                <div className={`grid gap-3 ${choix.length >= 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
                    {choix.map((c) => (
                        <Choix key={c.titre} {...c} />
                    ))}
                </div>
                {etape === "employeur" && (
                    <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">{t.prive}</p>
                )}
            </Ecran>
        );
    }

    /* ---------------------------------------------------------------- */
    /* Le résultat                                                       */
    /* ---------------------------------------------------------------- */

    if (!conclusion) {
        return (
            <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.manquesTitre} phases={t.phases} sens={sens} reduit={reduit}>
                <div className="mt-4">
                    <Liste items={controle.manques.map((cle) => t[`${cle}Titre`])} />
                    <Avancer pleineLargeur onClick={retour}>
                        {t.retour}
                    </Avancer>
                </div>
            </Resultat>
        );
    }

    const ton = TONS[conclusion.statut];
    const renvois = (RENVOIS[conclusion.statut] || [])
        .map((cle) => outils[cle])
        .filter(Boolean);

    return (
        <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            <div className="mt-4">
                <div className="flex items-start gap-3 mb-6">
                    <SolarIcon name={ton.icone} className={`text-2xl shrink-0 ${ton.couleur}`} />
                    <div className="font-bold text-lg dark:text-white leading-snug">
                        {t[`verdict${majuscule(conclusion.statut)}`]}
                    </div>
                </div>

                <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-1">
                    {t.motifsTitre}
                </div>
                <Liste items={conclusion.motifs.map(phrase)} />

                {conclusion.demarches.length > 0 && (
                    <div className="mt-8">
                        <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-1">
                            {t.demarchesTitre}
                        </div>
                        <Liste items={conclusion.demarches.map(phrase)} />
                    </div>
                )}

                {conclusion.alertes.length > 0 && (
                    <div className="mt-6 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4">
                        <div className="flex items-center gap-2.5 mb-2">
                            <SolarIcon
                                name="bell-bing-linear"
                                className="text-lg shrink-0 text-amber-600 dark:text-amber-400"
                            />
                            <div className="font-semibold dark:text-white leading-snug">{t.alertesTitre}</div>
                        </div>
                        <ul className="space-y-2 mb-0">
                            {conclusion.alertes.map((cle) => (
                                <li key={cle} className="text-sm leading-7 text-gray-700 dark:text-lightBlue">
                                    {phrase(cle)}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {conclusion.statut !== "hors-sujet" && (
                    <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mt-6 mb-0">
                        {t.exclues}
                    </p>
                )}

                {renvois.length > 0 && (
                    <div className="mt-8">
                        <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-3">
                            {t.suiteTitre}
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            {renvois.map((o) => (
                                <Link
                                    key={o.slug}
                                    href={{ pathname: "/tools/[slug]", params: { slug: o.slug } }}
                                    className="rounded-xl border border-gray-200/70 dark:border-white/10 px-4 py-3 text-sm font-semibold text-primary hover:!text-primary hover:border-primary/40 transition-colors duration-200"
                                >
                                    {o.nom} →
                                </Link>
                            ))}
                        </div>
                    </div>
                )}

                {recap && recap.length > 1 && (
                    <div className="mt-8">
                        <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-3">
                            {t.recapTitre}
                        </div>
                        <div className="rounded-2xl border border-gray-200/70 dark:border-white/10 overflow-hidden">
                            <table className="w-full text-sm">
                                <tbody>
                                    {recap.map(([cle, valeur]) => (
                                        <tr
                                            key={cle}
                                            className="border-b last:border-b-0 border-gray-200/70 dark:border-white/10"
                                        >
                                            <td className="px-5 py-3 text-gray-500 dark:text-lightBlue/70 align-top w-2/5">
                                                {t[`recap${majuscule(cle)}`]}
                                            </td>
                                            <td className="px-5 py-3 font-medium dark:text-white">
                                                {remplir(t[`${cle}${majuscule(valeur)}Titre`], VALEURS)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-8 mb-0">{t.source}</p>
                <Recommencer onClick={recommencer} libelle={t.recommencer} />
            </div>
        </Resultat>
    );
}

/** Les réponses de chaque écran, dans l'ordre, avec leur icône. */
const CHOIX = {
    employeur: [
        { valeur: "prive", icone: "buildings-2-linear" },
        { valeur: "public", icone: "city-linear" },
        { valeur: "aucun", icone: "user-linear" },
    ],
    temps: [
        { valeur: "plein", icone: "clock-circle-linear" },
        { valeur: "partiel", icone: "watch-square-minimalistic-linear" },
    ],
    exclusivite: [
        { valeur: "oui", icone: "lock-keyhole-linear" },
        { valeur: "non", icone: "lock-keyhole-unlocked-linear" },
        { valeur: "inconnu", icone: "question-circle-linear" },
    ],
    concurrence: [
        { valeur: "oui", icone: "users-group-two-rounded-linear" },
        { valeur: "non", icone: "check-circle-linear" },
        { valeur: "inconnu", icone: "question-circle-linear" },
    ],
    moment: [
        { valeur: "hors", icone: "moon-stars-linear" },
        { valeur: "pendant", icone: "sun-linear" },
    ],
    quotite: [
        { valeur: "plein", icone: "clock-circle-linear" },
        { valeur: "reduit", icone: "watch-square-minimalistic-linear" },
    ],
    nature: [
        { valeur: "entreprise", icone: "rocket-linear" },
        { valeur: "accessoire", icone: "book-linear" },
        { valeur: "vente-biens", icone: "box-linear" },
    ],
};

/** « vente-biens » devient « VenteBiens », pour aller chercher son libellé. */
function majuscule(valeur) {
    return valeur
        .split("-")
        .map((mot) => mot.charAt(0).toUpperCase() + mot.slice(1))
        .join("");
}
